import { BaseTable, Loading, TableHeader } from "components"
import moment from "moment"
import { convertNumber } from "utils"
import type { ItemBuildReportRow } from "../types/itemBuildReport"
import {
  formatReportBarcode,
  getIngredientReportBarcode,
} from "../utils/itemBuildReportBarcode"

type ReportTableProps = {
  data: ItemBuildReportRow[]
  isLoading: boolean
  error?: string
}

/** Sama warna tab INVENTORY aktif (tailwind primary). */
const componentHeaderClass = "!bg-primary !text-black"

const currency = (value?: number | null) =>
  convertNumber(Number(value ?? 0)).intoCurrency ?? "0"

/** Border tebal per 1 build item — kolom kiri (rowSpan) dibungkus atas+bawah sekaligus. */
const groupHeaderCellBorder =
  "!border-t-2 !border-t-neutral/40 !border-b-2 !border-b-neutral/40"

const groupComponentCellBorder = (index: number, totalRows: number) => {
  const classes: string[] = []
  if (index === 0) classes.push("!border-t-2 !border-t-neutral/40")
  if (index === totalRows - 1) classes.push("!border-b-2 !border-b-neutral/40")
  return classes.join(" ")
}

export function ReportTable({ data, isLoading, error }: ReportTableProps) {
  const colSpan = 19

  return (
    <div className="overflow-x-auto max-h-[calc(100vh-480px)]">
      <BaseTable className="border-collapse text-sm">
        <TableHeader className="uppercase">
          <tr>
            <th rowSpan={2} className="text-center w-12">
              NO
            </th>
            <th rowSpan={2}>DATE</th>
            <th rowSpan={2}>BUILD ID</th>
            <th rowSpan={2}>CODE</th>
            <th rowSpan={2}>DESCRIPTION</th>
            <th rowSpan={2}>CATEGORY</th>
            <th rowSpan={2}>QTY BUILD</th>
            <th rowSpan={2}>SOH</th>
            <th rowSpan={2}>UNIT</th>
            <th rowSpan={2}>COMPONENT AT COST</th>
            <th rowSpan={2}>OVERHEAD</th>
            <th rowSpan={2}>AVERAGE PRICE</th>
            <th rowSpan={2}>PIC</th>
            <th colSpan={6} className={`${componentHeaderClass} text-center`}>
              COMPONENT
            </th>
          </tr>
          <tr>
            <th className={componentHeaderClass}>CODE</th>
            <th className={componentHeaderClass}>DESCRIPTION</th>
            <th className={componentHeaderClass}>USED</th>
            <th className={componentHeaderClass}>UNIT</th>
            <th className={componentHeaderClass}>PRICE</th>
            <th className={componentHeaderClass}>COST</th>
          </tr>
        </TableHeader>
        {isLoading ? (
          <tbody>
            <tr>
              <td colSpan={colSpan} className="text-center">
                <Loading loading />
              </td>
            </tr>
          </tbody>
        ) : error ? (
          <tbody>
            <tr>
              <td colSpan={colSpan} className="text-center text-error">
                {error}
              </td>
            </tr>
          </tbody>
        ) : data.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={colSpan} className="text-center">
                <Loading errorText="No Data Available" />
              </td>
            </tr>
          </tbody>
        ) : (
          data.map((row, rowIndex) => (
            <tbody key={row.build_id}>
              <BuildReportRows row={row} rowNumber={rowIndex + 1} />
            </tbody>
          ))
        )}
      </BaseTable>
    </div>
  )
}

function BuildReportRows({
  row,
  rowNumber,
}: {
  row: ItemBuildReportRow
  rowNumber: number
}) {
  const ingredients = row.ingredients?.length ? row.ingredients : [null]
  const unitLabel = row.unit_sell_measure_name ?? row.unit_measure_name ?? "-"
  const rowSpan = ingredients.length
  const totalRows = ingredients.length
  const productBarcode = formatReportBarcode(row.product_barcode)

  return (
    <>
      {ingredients.map((ingredient, index) => {
        const componentBorder = groupComponentCellBorder(index, totalRows)
        const ingredientBarcode = getIngredientReportBarcode(ingredient)

        return (
          <tr key={`${row.build_id}-${index}`}>
            {index === 0 ? (
              <>
                <td
                  rowSpan={rowSpan}
                  className={`align-top text-center ${groupHeaderCellBorder}`}
                >
                  {rowNumber}
                </td>
                <td
                  rowSpan={rowSpan}
                  className={`align-top whitespace-nowrap ${groupHeaderCellBorder}`}
                >
                  {row.transaction_date
                    ? moment(row.transaction_date).format("DD/MM/YYYY")
                    : "-"}
                </td>
                <td
                  rowSpan={rowSpan}
                  className={`align-top whitespace-nowrap ${groupHeaderCellBorder}`}
                >
                  {row.trx_code ?? "-"}
                </td>
                <td rowSpan={rowSpan} className={`align-top ${groupHeaderCellBorder}`}>
                  {productBarcode}
                </td>
                <td
                  rowSpan={rowSpan}
                  className={`align-top min-w-[160px] ${groupHeaderCellBorder}`}
                >
                  {row.product_name ?? "-"}
                </td>
                <td rowSpan={rowSpan} className={`align-top ${groupHeaderCellBorder}`}>
                  {row.mr_product_category_name ?? "-"}
                </td>
                <td
                  rowSpan={rowSpan}
                  className={`align-top text-right ${groupHeaderCellBorder}`}
                >
                  {row.qty_build_unit ?? 0}
                </td>
                <td
                  rowSpan={rowSpan}
                  className={`align-top text-right ${groupHeaderCellBorder}`}
                >
                  {currency(row.soh)}
                </td>
                <td rowSpan={rowSpan} className={`align-top ${groupHeaderCellBorder}`}>
                  {unitLabel}
                </td>
                <td
                  rowSpan={rowSpan}
                  className={`align-top text-right ${groupHeaderCellBorder}`}
                >
                  {currency(row.component_at_cost)}
                </td>
                <td
                  rowSpan={rowSpan}
                  className={`align-top text-right ${groupHeaderCellBorder}`}
                >
                  {currency(row.overhead)}
                </td>
                <td
                  rowSpan={rowSpan}
                  className={`align-top text-right ${groupHeaderCellBorder}`}
                >
                  {currency(row.avg_price)}
                </td>
                <td rowSpan={rowSpan} className={`align-top ${groupHeaderCellBorder}`}>
                  {row.employee_name ?? "-"}
                </td>
              </>
            ) : null}
            <td className={componentBorder}>{ingredientBarcode}</td>
            <td className={componentBorder}>{ingredient?.product_ingredient_name ?? "-"}</td>
            <td className={`text-right ${componentBorder}`}>
              {ingredient?.qty_ingredient_unit ?? "-"}
            </td>
            <td className={componentBorder}>{ingredient?.mr_unit_name ?? "-"}</td>
            <td className={`text-right ${componentBorder}`}>
              {ingredient != null ? currency(ingredient.avg_price) : "-"}
            </td>
            <td className={`text-right ${componentBorder}`}>
              {ingredient != null ? currency(ingredient.amount) : "-"}
            </td>
          </tr>
        )
      })}
    </>
  )
}
